import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import moment from "moment";

import PageTitle from "../../../layouts/PageTitle";
import {
  useGetDailyMaterialReportQuery,
  useGetWorkProgressReportQuery,
} from "../../../../store/appApi";
import { selectHasModuleAccess } from "../../../../store/authSlice";

const AccordionSection = ({ letter, title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="mb-2 rounded overflow-hidden"
      style={{ border: "1px solid #c8d8cb" }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-100 d-flex align-items-center justify-content-between px-3 py-2 border-0"
        style={{
          background: open ? "#324336" : "#eef4ef",
          color: open ? "#fff" : "#324336",
          cursor: "pointer",
          transition: "background 0.18s",
        }}
      >
        <span className="fw-semibold" style={{ fontSize: 13 }}>
          <span
            className="me-2 px-2 py-0 rounded"
            style={{
              background: open
                ? "rgba(255,255,255,0.2)"
                : "rgba(50,67,54,0.15)",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {letter}
          </span>
          {title}
        </span>
        <i
          className={`fa fa-chevron-${open ? "up" : "down"}`}
          style={{ fontSize: 11 }}
        />
      </button>
      {open && <div className="bg-white">{children}</div>}
    </div>
  );
};

const Empty = () => (
  <p className="text-muted mb-0 px-3 py-3 small">— No entries recorded —</p>
);

const DetailDailyMaterial = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasRead = useSelector(selectHasModuleAccess("workProgress", "read"));
  const hasWrite = useSelector(
    selectHasModuleAccess("workProgress", "read-write"),
  );

  const { data, isLoading, isError } = useGetDailyMaterialReportQuery(id, {
    skip: !hasRead,
  });
  const r = data?.report;

  // Fetch morning report only when linked
  const { data: morningData, isLoading: morningLoading } =
    useGetWorkProgressReportQuery(r?.morningReportId, {
      skip: !r?.morningReportId,
    });
  const mr = morningData?.report;

  const fmtDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { dateStyle: "long" }) : "—";

  const matName = (m) => m?.materialId?.skuName ?? m?.materialName ?? "—";
  const matUnit = (m) => m?.materialId?.measurement ?? m?.measurement ?? "—";
  const contractorName = (m) => m?.issuedToContractorId?.name ?? "—";

  if (!hasRead) {
    return (
      <>
        <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
            <h5>Access Restricted</h5>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />
        <div className="text-center py-5">
          <span className="spinner-border" />
        </div>
      </>
    );
  }

  if (isError || !r) {
    return (
      <>
        <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />
        <div className="alert alert-danger">Failed to load report.</div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Report Detail" motherMenu="Daily Progress" />

      {/* ── Meta card ─────────────────────────────────────────────────────── */}
      <div className="card mb-4" style={{ background: "#f8f9fa" }}>
        <div className="card-body d-flex flex-wrap align-items-center gap-4 py-3">
          <div>
            <span className="text-muted small d-block">Date</span>
            <span className="fw-bold fs-5">{fmtDate(r.date)}</span>
          </div>
          <div>
            <span className="text-muted small d-block">Reported By</span>
            <span className="fw-semibold">{r.reportedBy?.name ?? "—"}</span>
          </div>
          <div>
            <span className="text-muted small d-block">Morning Report</span>
            {r.morningReportId ? (
              <Link
                to={`/progress/workprogress/detail/${r.morningReportId}`}
                className="badge badge-success light text-decoration-none"
              >
                View Morning Report ↗
              </Link>
            ) : (
              <span className="badge badge-secondary light">Manual entry</span>
            )}
          </div>
          <div className="ms-auto d-flex gap-2">
            {hasWrite && (
              <button
                className="btn btn-sm btn-outline-warning"
                onClick={() => navigate(`/progress/daily-material/edit/${id}`)}
              >
                <i className="fa fa-pencil me-1" />
                Edit
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
        </div>
      </div>

      {/* ── Morning report sections (if linked) ──────────────────────────── */}
      {r.morningReportId && (
        <div
          className="card mb-4"
          style={{ border: "2px solid #324336", borderRadius: 8 }}
        >
          {/* Accordion header */}
          <div
            className="card-header d-flex align-items-center justify-content-between py-3"
            style={{ background: "#324336", borderRadius: "6px 6px 0 0" }}
          >
            <div className="d-flex align-items-center gap-2">
              <i
                className="fa fa-sun-o text-warning"
                style={{ fontSize: 16 }}
              />
              <span className="fw-bold text-white" style={{ fontSize: 14 }}>
                Morning Report Details
              </span>
              {morningLoading && (
                <span className="spinner-border spinner-border-sm text-light ms-1" />
              )}
              {mr && (
                <span className="text-white-50 small ms-2">
                  {moment(mr.reportDateTime).format("DD MMM YYYY — hh:mm A")}
                  {mr.reportedBy?.name && ` · ${mr.reportedBy.name}`}
                </span>
              )}
            </div>
            <Link
              to={`/progress/workprogress/detail/${r.morningReportId}`}
              className="btn btn-sm btn-light"
              style={{ fontSize: 11 }}
            >
              Open Full Report ↗
            </Link>
          </div>

          {/* Accordion sections */}
          {mr && (
            <div className="card-body" style={{ background: "#f4f8f5" }}>
              {/* Equipment */}
              <AccordionSection letter="A" title="Equipment Deployment">
                {mr.equipments?.length ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Equipment</th>
                          <th>Type</th>
                          <th>Number</th>
                          <th>Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mr.equipments.map((e, i) => (
                          <tr key={i}>
                            <td className="text-muted small">{i + 1}</td>
                            <td>{e.equipmentId?.eqpName ?? "—"}</td>
                            <td className="text-muted small">
                              {e.equipmentId?.eqpType ?? "—"}
                            </td>
                            <td className="text-muted small">
                              {e.equipmentId?.eqpNumber ?? "—"}
                            </td>
                            <td>{e.eqpQuantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty />
                )}
              </AccordionSection>

              {/* Work Execution */}
              <AccordionSection
                letter="B"
                title="Work Execution &amp; Manpower"
              >
                {mr.workExecution?.length ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Type of Work</th>
                          <th>Labour Engaged</th>
                          <th>No. of Labour</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mr.workExecution.map((w, i) => (
                          <tr key={i}>
                            <td className="text-muted small">{i + 1}</td>
                            <td>{w.typeOfWork || "—"}</td>
                            <td>
                              {w.labourEngaged ? (
                                <span className="badge badge-primary light">
                                  {w.labourEngaged}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>{w.numberOfLabour}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty />
                )}
              </AccordionSection>

              {/* Contractor Engagement */}
              <AccordionSection letter="C" title="Contractor Engagement">
                {mr.contractorDetails?.length ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Contractor</th>
                          <th>Phone</th>
                          <th>Skilled</th>
                          <th>Unskilled</th>
                          <th>Work Assigned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mr.contractorDetails.map((c, i) => (
                          <tr key={i}>
                            <td className="text-muted small">{i + 1}</td>
                            <td className="fw-semibold">
                              {c.contractorId?.name ?? "—"}
                            </td>
                            <td className="text-muted small">
                              {c.contractorId?.phone ?? "—"}
                            </td>
                            <td>{c.skilledLabour}</td>
                            <td>{c.unskilledLabour}</td>
                            <td>{c.workAssigned || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty />
                )}
              </AccordionSection>

              {/* Internal Staff */}
              <AccordionSection letter="D" title="Internal Staff Deployment">
                {mr.employeeDetails?.length ? (
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mr.employeeDetails.map((emp, i) => (
                          <tr key={i}>
                            <td className="text-muted small">{i + 1}</td>
                            <td className="fw-semibold">
                              {emp.employeeId?.name ?? "—"}
                            </td>
                            <td>
                              {emp.employeeId?.role ? (
                                <span className="badge badge-secondary light">
                                  {emp.employeeId.role}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td>{emp.locationName || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Empty />
                )}
              </AccordionSection>
            </div>
          )}
        </div>
      )}

      {/* ── Material entries (from this daily report) ─────────────────────── */}
      <h6
        className="text-muted fw-semibold mb-3"
        style={{ letterSpacing: 0.5 }}
      >
        DAILY MATERIAL ENTRIES
      </h6>

      <div className="card mb-3">
        <div className="card-header d-flex align-items-center gap-2">
          <h5 className="card-title mb-0">Material Entries</h5>
          <span className="badge badge-primary light ms-1">
            {r.materials?.length ?? 0} rows
          </span>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Material</th>
                  <th>Issued To</th>
                  <th>Unit</th>
                  <th>Qty Issued</th>
                  <th>Qty Returned</th>
                  <th>Source</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(r.materials ?? []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-4 text-muted">
                      No material entries.
                    </td>
                  </tr>
                )}
                {(r.materials ?? []).map((m, i) => (
                  <tr key={i}>
                    <td className="text-muted small">{i + 1}</td>
                    <td className="fw-semibold">{matName(m)}</td>
                    <td className="text-muted small">{contractorName(m)}</td>
                    <td className="text-muted small">{matUnit(m)}</td>
                    <td>
                      <span className="badge badge-danger light">
                        {m.qtyIssued ?? 0}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-success light">
                        {m.qtyReturned ?? 0}
                      </span>
                    </td>
                    <td>
                      {m.fromMorningReport ? (
                        <span
                          className="badge bg-info text-dark"
                          style={{ fontSize: 10 }}
                        >
                          Morning
                        </span>
                      ) : (
                        <span
                          className="badge bg-secondary"
                          style={{ fontSize: 10 }}
                        >
                          Manual
                        </span>
                      )}
                    </td>
                    <td className="text-muted small">{m.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Summary ───────────────────────────────────────────────────────── */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="card-title mb-0">Daily Summary</h5>
          <small className="text-muted ms-2">
            Unique material totals for the day
          </small>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Material</th>
                  <th>Unit</th>
                  <th>Total Qty Issued</th>
                  <th>Total Qty Returned</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {(r.summary ?? []).length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted">
                      No summary data.
                    </td>
                  </tr>
                )}
                {(r.summary ?? []).map((s, i) => (
                  <tr key={i}>
                    <td className="text-muted small">{i + 1}</td>
                    <td className="fw-semibold">
                      {s.materialId?.skuName ?? s.materialName ?? "—"}
                    </td>
                    <td className="text-muted small">
                      {s.materialId?.measurement ?? s.measurement ?? "—"}
                    </td>
                    <td>
                      <span className="badge badge-danger light">
                        {s.qtyIssued ?? 0}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-success light">
                        {s.qtyReturned ?? 0}
                      </span>
                    </td>
                    <td className="text-muted small">{s.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default DetailDailyMaterial;
